import { useFormik } from 'formik'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, FormFeedback, Row } from 'reactstrap'
import * as Yup from 'yup'
import documents from '../../api/documents/hooks'
import { DocumentFile } from '../../api/documents/types'
import Dropzone from '../Dropzone/Dropzone'
import RecursiveForm from '../RecursiveForm/RecursiveForm'
import { useDocumentFormConfig, useDocumentFormSelectOptions } from './hooks'
import { buildFormData, buildUpdatePayload } from './utils'

interface DocumentFormValues {
    id?: string | number
    document_type_id?: string | number
    document_creation_date?: string | null
    document_validity_date?: string | null
    file_description?: string
    file_sync?: boolean | number
    document_project_internalAppId?: string | number
    document_property_internalAppId?: string | number
    document_contact_id?: string | number
    document_contact_internalAppId?: string | number
}

export interface DocumentFormProps {
    mode: 'create' | 'edit'
    documentData?: DocumentFile
    parentId?: string | number
    documentModule?: string
    contactType?: string
    onSaved?: () => void
}

const DocumentForm = (props: DocumentFormProps) => {
    const { mode, documentData, parentId, documentModule, contactType, onSaved } = props

    const { t } = useTranslation()
    const pendingFile = useRef<File | null>(null)
    const [parentError, setParentError] = useState<string | null>(null)

    const disableIdFields = mode === 'edit' || Boolean(parentId)
    const configData = mode === 'edit' ? documentData : { module: documentModule }

    const { documentInputs, documentValidationConfig } = useDocumentFormConfig(configData, disableIdFields)
    const { selectOptions, isPending } = useDocumentFormSelectOptions(parentId, contactType, !contactType ? documentData?.parentElement : null)

    const { mutate: uploadDocument } = documents.UploadDocument.useMutation()
    const { mutate: updateDocument } = documents.UpdateDocument.useMutation()

    const validationSchema = useMemo(
        () =>
            Yup.object({
                document_type_id: Yup.string().required(t('form.errors.documents.document_type_id')),
                ...(mode === 'edit' ? { file_description: Yup.string().required(t('form.errors.documents.file_description')) } : {}),
            }),
        [mode, t]
    )

    const formik = useFormik<DocumentFormValues>({
        enableReinitialize: true,
        initialValues: documentValidationConfig.initialValues ?? {},
        validationSchema,
        onSubmit: (values) => {
            if (mode === 'create') {
                const file = pendingFile.current
                if (!file) return

                const uploadPayload = buildFormData({ file, values, parentId })

                uploadDocument(uploadPayload, {
                    onSuccess: () => {
                        formik.resetForm()
                        pendingFile.current = null
                        onSaved?.()
                    },
                })

                return
            }

            const updatePayload = buildUpdatePayload({ values, documentData })

            updateDocument(updatePayload, {
                onSuccess: () => onSaved?.(),
            })
        },
    })

    const handleUpload = (files: File[]) => {
        const hasParentSelected = Boolean(
            formik.values.document_project_internalAppId || formik.values.document_property_internalAppId || formik.values.document_contact_id
        )

        if (!parentId && !hasParentSelected) {
            setParentError(t('form.errors.documents.general'))
            return
        }

        setParentError(null)
        pendingFile.current = files[0]
        formik.submitForm()
    }

    return (
        <>
            {mode === 'create' && parentError && (
                <Row>
                    <FormFeedback type="invalid" className="d-block text-center my-2">
                        {parentError}
                    </FormFeedback>
                </Row>
            )}

            <RecursiveForm
                inputs={documentInputs?.sideForm}
                formik={formik}
                selectOptions={selectOptions}
                selectOptionsLoading={isPending}
                config={undefined}
                subform={undefined}
                inputComponents={undefined}
                fieldArrayDeleteComponent={undefined}
            />

            {mode === 'create' ? (
                <Dropzone tab="documents" module="" parent="" callback={handleUpload} />
            ) : (
                <div className="border-top border-top-dashed py-3">
                    <div className="hstack gap-2">
                        <Button
                            tag="a"
                            color="success"
                            onClick={() => formik.handleSubmit()}
                            className="btn-label"
                            data-testid="document-form-submit-btn"
                        >
                            <i className="mdi mdi-check align-bottom label-icon align-middle fs-16 me-2"></i>
                            {t('button.accept')}
                        </Button>
                    </div>
                </div>
            )}
        </>
    )
}

export default DocumentForm
